import { format } from 'date-fns';
import { formatISO } from 'date-fns';
import { isMatch } from 'date-fns';
import { de, fr, it } from 'date-fns/locale';
import { parse } from 'date-fns';
import { parseISO } from 'date-fns';
import { toDate } from 'date-fns';
import { isDefined } from '@/utils/assertions';

export type DateCalculation = (today: Date) => Date;

export function toTimestamp(value?: string | null): number | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  return parseISO(value).valueOf();
}

type FromTimestampValue<T> = T extends number ? string : T extends undefined ? undefined : never;

export function fromTimestamp<T extends number | undefined>(value: T): FromTimestampValue<T> {
  if (value === undefined) {
    return undefined as FromTimestampValue<T>;
  }
  return asIsoDate(toDate(value)) as FromTimestampValue<T>;
}

export function toTimestamps(values: readonly string[] | undefined): number[] {
  if (values === undefined) {
    return [];
  }
  return values.map(toTimestamp).filter(isDefined);
}

export function toTimestampsString(values: readonly string[] | undefined): string {
  return toTimestamps(values).join(',');
}

export function fromTimestamps(values: readonly number[]): string[] {
  return values.map(v => fromTimestamp(v));
}

export function isEquivalent(first: readonly string[], other: readonly string[]) {
  if (first.length !== other.length) {
    return false;
  }
  return first.every(value => other.includes(value));
}

export function asIsoDate(value: Date) {
  return formatISO(value, { representation: 'date' });
}

export function fromIsoToDate(value: string | undefined): Date | undefined {
  if (value === undefined) {
    return undefined;
  }
  return parseISO(value);
}
export function isIsoDate(text: string) {
  return isMatch(text, 'yyyy-MM-dd');
}

export function withoutTimePart(text: string) {
  if (text.includes('T')) {
    return text.split('T')[0];
  }
  return text;
}

export function supportedDateFormats(...customDateFormats: string[]) {
  const defaultsDateFormats = [
    'dd/MM/yyyy',
    'dd-MM-yyyy',
    'dd.MM.yyyy',
    'ddMMyyyy',
    'MM/dd/yyyy',
    'MM-dd-yyyy',
    'MM.dd.yyyy',
    'MMddyyyy',
    'yyyy/MM/dd',
    'yyyy-MM-dd',
    'yyyy.MM.dd',
    'yyyyMMdd',
    'd-MM-yy',
    'LLL dd, yyyy',
    'yyyy年MM月dd日',
    'dd MMM yyyy',
    'dd. MMM yyyy',
  ];
  return [...customDateFormats, ...defaultsDateFormats];
}

export function guessDateFormats(datesToParse: string[], dateFormats: string[]) {
  return dateFormats.filter(fmt =>
    datesToParse.every(
      candidate =>
        isMatch(candidate, fmt) ||
        isMatch(candidate, fmt, { locale: fr }) ||
        isMatch(candidate, fmt, { locale: de }) ||
        isMatch(candidate, fmt, { locale: it }),
    ),
  );
}

export function userInputToIsoDate(text: string, dateFormat: string): string | undefined {
  if (isIsoDate(text)) {
    return text;
  }
  if (isMatch(text, dateFormat)) {
    const parsed = parse(text, dateFormat, new Date());
    return asIsoDate(parsed);
  }
  if (isMatch(text, dateFormat, { locale: fr })) {
    const parsed = parse(text, dateFormat, new Date(), { locale: fr });
    return asIsoDate(parsed);
  }
  if (isMatch(text, dateFormat, { locale: de })) {
    const parsed = parse(text, dateFormat, new Date(), { locale: de });
    return asIsoDate(parsed);
  }
  if (isMatch(text, dateFormat, { locale: it })) {
    const parsed = parse(text, dateFormat, new Date(), { locale: it });
    return asIsoDate(parsed);
  }
}

export function toDateString(value: Date | number | undefined, dateFormat = 'yyyy-MM-dd'): string {
  return value === undefined ? '' : format(value, dateFormat);
}
